import React, { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import {
  House, Clock, CalendarCheck, CheckSquare, Users,
  ChartBar, ShieldCheck, Gear, SignOut, Fingerprint, MonitorPlay,
  Calendar, Warning, List, X, DeviceMobile, Monitor as MonitorIcon, DeviceTablet, DownloadSimple,
} from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import { useI18n, LangSwitch } from "@/lib/i18n";

const items = [
  { to: "dashboard", key: "dashboard", icon: House, roles: ["employee","manager","hr","admin"] },
  { to: "monitor", key: "monitor", icon: MonitorPlay, roles: ["manager","hr","admin"] },
  { to: "monthly-card", key: "monthly_card", icon: Calendar, roles: ["manager","hr","admin"] },
  { to: "my-status", key: "my_status", icon: Clock, roles: ["employee","manager","hr","admin"] },
  { to: "leave", key: "leave", icon: CalendarCheck, roles: ["employee","manager","hr","admin"] },
  { to: "approvals", key: "approvals", icon: CheckSquare, roles: ["manager","hr","admin"] },
  { to: "employees", key: "employees", icon: Users, roles: ["hr","admin"] },
  { to: "reports", key: "reports", icon: ChartBar, roles: ["manager","hr","admin"] },
  { to: "anomalies", key: "anomalies", icon: Warning, roles: ["manager","hr","admin"] },
  { to: "audit", key: "audit", icon: ShieldCheck, roles: ["hr","admin"] },
  { to: "settings", key: "settings", icon: Gear, roles: ["employee","manager","hr","admin"] },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [modeOpen, setModeOpen] = useState(false);
  const [installEvt, setInstallEvt] = useState(null);

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallEvt(e); };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const installPWA = async () => {
    if (!installEvt) return;
    installEvt.prompt();
    try { await installEvt.userChoice; } catch {}
    setInstallEvt(null);
    setModeOpen(false);
  };

  return (
    <div className="min-h-screen md:grid md:grid-cols-[260px_1fr] bg-[#F4F4F0]">
      {/* Mobile top bar */}
      <div className="md:hidden sticky top-0 z-30 bg-white brut-border border-l-0 border-r-0 border-t-0 flex items-center justify-between p-3">
        <button data-testid="sidebar-toggle" onClick={() => setSidebarOpen(true)} className="brut-border p-2 bg-white hover:bg-[#FFCC00]">
          <List size={20} weight="bold" />
        </button>
        <div className="flex items-center gap-2">
          <Fingerprint size={22} weight="bold" />
          <div className="font-display font-black text-lg tracking-tighter">ATTENDIO</div>
        </div>
        <button data-testid="mobile-mode-btn" onClick={() => setModeOpen(true)} className="brut-border p-2 bg-[#FFCC00] font-bold text-xs uppercase tracking-wider">
          <DeviceMobile size={18} weight="bold" />
        </button>
      </div>

      {/* Sidebar (desktop static, mobile drawer) */}
      <aside
        data-testid="sidebar"
        className={`${sidebarOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 fixed md:static top-0 left-0 w-[280px] md:w-auto h-screen md:h-auto z-40 bg-white brut-border border-l-0 border-t-0 border-b-0 flex flex-col transition-transform`}
      >
        <div className="p-6 border-b-2 border-black flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Fingerprint size={28} weight="bold" />
            <div>
              <div className="font-display font-black text-xl leading-none tracking-tighter">ATTENDIO</div>
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-black/60 mt-1">HR Control Room</div>
            </div>
          </div>
          <button data-testid="sidebar-close" onClick={() => setSidebarOpen(false)} className="md:hidden brut-border p-1 bg-white">
            <X size={16} weight="bold" />
          </button>
        </div>
        <nav className="flex-1 p-3 flex flex-col gap-1 overflow-y-auto">
          {items
            .filter((i) => i.roles.includes(user?.role))
            .map((i) => (
              <NavLink
                key={i.to}
                to={i.to}
                data-testid={`nav-${i.to}`}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 brut-border uppercase text-xs font-bold tracking-wider ${
                    isActive ? "bg-black text-white" : "bg-white text-black hover:bg-[#FFCC00]"
                  }`
                }
              >
                <i.icon size={18} weight="bold" />
                {t(i.key)}
              </NavLink>
            ))}
        </nav>
        <div className="p-3 border-t-2 border-black flex flex-col gap-2">
          <div className="brut-border bg-[#F4F4F0] p-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-black/60">{t("role")}</div>
            <div className="font-display font-black text-lg leading-tight" data-testid="me-name">{user?.name}</div>
            <div className="text-xs font-mono break-all">{user?.email}</div>
            <div className="mt-1 inline-block tag bg-[#FFCC00]" data-testid="me-role">{t(user?.role)}</div>
          </div>
          <button
            data-testid="app-mode-btn"
            onClick={() => { setModeOpen(true); setSidebarOpen(false); }}
            className="flex items-center justify-center gap-2 bg-[#FFCC00] text-black brut-border brut-shadow brut-hover py-2 uppercase text-xs font-bold tracking-wider"
          >
            <DeviceMobile size={16} weight="bold" /> {t("app_mode")}
          </button>
          <LangSwitch />
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="flex items-center justify-center gap-2 bg-[#FF3333] text-white brut-border brut-shadow brut-hover py-2 uppercase text-xs font-bold tracking-wider"
          >
            <SignOut size={16} weight="bold" /> {t("logout")}
          </button>
        </div>
      </aside>

      {/* Backdrop for mobile drawer */}
      {sidebarOpen && (
        <div
          data-testid="sidebar-backdrop"
          onClick={() => setSidebarOpen(false)}
          className="md:hidden fixed inset-0 bg-black/40 z-30"
        />
      )}

      {/* Main */}
      <main className="p-4 md:p-6 overflow-y-auto md:max-h-screen">
        <Outlet />
      </main>

      {/* Mode picker dialog */}
      {modeOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setModeOpen(false)}>
          <div data-testid="app-mode-dialog" onClick={(e) => e.stopPropagation()} className="brut-border bg-white p-6 brut-shadow-lg max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-black/60">{t("app_mode")}</div>
                <div className="font-display font-black text-2xl tracking-tighter">{t("app_mode_pick")}</div>
              </div>
              <button type="button" onClick={() => setModeOpen(false)}><X size={20} weight="bold" /></button>
            </div>
            <div className="grid grid-cols-1 gap-3">
              <Link to="/app/dashboard" data-testid="mode-dialog-desktop" onClick={() => setModeOpen(false)} className="brut-border p-4 flex items-center gap-3 brut-hover bg-black text-white">
                <MonitorIcon size={28} weight="bold" />
                <div>
                  <div className="font-display font-black text-lg tracking-tighter">DESKTOP</div>
                  <div className="text-[11px] font-bold uppercase tracking-wider opacity-80">{t("mode_desktop_label")} — HR control room</div>
                </div>
              </Link>
              <Link to="/kiosk" data-testid="mode-dialog-kiosk" onClick={() => setModeOpen(false)} className="brut-border p-4 flex items-center gap-3 brut-hover bg-[#FFCC00] text-black">
                <DeviceTablet size={28} weight="bold" />
                <div>
                  <div className="font-display font-black text-lg tracking-tighter">KIOSK</div>
                  <div className="text-[11px] font-bold uppercase tracking-wider opacity-80">{t("mode_kiosk_label")} — NFC tap station</div>
                </div>
              </Link>
              <button data-testid="mode-dialog-pwa" onClick={installPWA} disabled={!installEvt} className="brut-border p-4 flex items-center gap-3 brut-hover bg-[#FF66B2] text-black disabled:opacity-60 disabled:cursor-not-allowed text-left">
                <DownloadSimple size={28} weight="bold" />
                <div>
                  <div className="font-display font-black text-lg tracking-tighter">PWA</div>
                  <div className="text-[11px] font-bold uppercase tracking-wider opacity-80">
                    {installEvt ? t("mode_pwa_install") : t("mode_pwa_installed_or_unsupported")}
                  </div>
                </div>
              </button>
            </div>
            {!installEvt && (
              <div className="mt-4 text-[11px] text-black/60 leading-relaxed" data-testid="mode-pwa-fallback">
                {t("mode_pwa_fallback_hint")}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
